import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { trackEvent } from '../utils/analytics';
import { 
  OrganizationStructuredData, 
  MedicalBusinessStructuredData, 
  WebSiteStructuredData,
  FAQStructuredData 
} from '../components/StructuredData';
import { 
  Bot, 
  BarChart3, 
  TrendingUp, 
  Shield, 
  Lightbulb, 
  Users,
  Brain,
  FileText,
  Zap,
  Clock,
  Target,
  Award,
  Eye,
  Rocket,
  CheckCircle,
  UserPlus,
  LogIn,
  Heart,
  Flower,
  SquareUser,
  MessageCircle,
  Star,
  Quote,
  ArrowRight,
  Calendar,
  Phone,
  Video,
  Mail,
  Globe,
  Smartphone,
  Download,
  Headphones,
  BookOpen,
  Sparkles,
  Lock,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Timer,
  DollarSign,
  Stethoscope,
  HeadphonesIcon,
  Activity,
  FileCheck,
  UserCheck,
  Pill,
  TrendingDown,
  CreditCard,
  Building,
  BadgeCheck,
  ChevronRight,
  Plus,
  Minus,
  Coffee,
  Train,
  Trees,
  Earth,
  Briefcase,
  Battery
} from 'lucide-react';
import MobileMenu from '../components/MobileMenu';
import Icon from '../components/Icon';
import Logo from '../components/Logo';
import Card from '../ui/Card';

const WorkLifeBalancePage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  // SEO optimization for work-life balance landing page
  useSEO({
    title: 'Work-Life Balance & Burnout Prevention - AI-terapi för anställda | Your Therapist',
    description: 'Förebygg utmattning och förbättra work-life balance med AI-driven terapi. Specialiserat för anställda som upplever stress, överarbete och svårigheter att koppla av.',
    keywords: 'work-life balance, burnout prevention, utmattning förebygga, arbetsstress, anställd terapi, workplace mental health, stress management, överarbete hjälp',
    image: 'https://www.yourtherapist.se/logo512.png',
    url: 'https://www.yourtherapist.se/work-life-balance',
    type: 'website'
  });

  const handleGetStarted = () => {
    // Track the click event
    trackEvent('Start Chat Button Click', { location: 'work-life-balance-page' });
    navigate('/instant-chat?utm_source=work-life-balance&utm_campaign=employee-wellness');
    // Scroll to top after navigation
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleContactUs = () => {
    // Track the click event
    trackEvent('Contact Link Click', { location: 'work-life-balance-page' });
    navigate('/contact?utm_source=work-life-balance');
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // FAQ Data specifically for work-life balance
  const workLifeFaqData = [
    {
      question: "Hur vet jag om jag är på väg mot utmattning?",
      answer: "Varningstecken inkluderar kronisk trötthet som inte försvinner med vila, minskad arbetsprestation, cynism mot jobbet, fysiska symptom som huvudvärk eller magproblem, och svårigheter att koppla av även på fritiden. Vår AI kan hjälpa dig identifiera tidiga tecken och utveckla strategier."
    },
    {
      question: "Kan jag chatta anonymt om arbetsstress?",
      answer: "Ja, absolut. Du kan börja helt anonymt utan att ange arbetsplats eller personuppgifter. Många uppskattar detta när de utforskar sina känslor kring arbetet innan de är redo att prata med någon de känner."
    },
    {
      question: "Hjälper det verkligen när jag har så lite tid?",
      answer: "Ja! Vår AI är tillgänglig 24/7, så du kan chatta när det passar dig - på väg till jobbet, under lunchpausen, eller sent på kvällen. Även 5-10 minuters reflektion kan ge värdefull insikt och stressreduktion."
    },
    {
      question: "Kommer min arbetsgivare få reda på att jag använder terapi?",
      answer: "Nej, din användning är helt privat och konfidentiell. Vi delar aldrig information med arbetsgivare eller andra tredje parter. Din mentala hälsa är din privata angelägenhet."
    },
    {
      question: "Kan jag få hjälp med att sätta gränser på jobbet?",
      answer: "Definitivt! Vi hjälper dig utveckla konkreta strategier för att sätta hälsosamma gränser, kommunicera dina behov professionellt, och hantera förväntningar från kollegor och chefer."
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-primary font-sans">
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .font-inter {
          font-family: 'Inter', sans-serif;
        }
        
        .animate-gradient {
          background: linear-gradient(-45deg, var(--brand-accent), var(--brand-primary), var(--error-500), var(--warning-500));
          background-size: 400% 400%;
          animation: gradientShift 8s ease infinite;
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        html {
          scroll-behavior: smooth;
        }
      `}</style>

      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-primary border-b border-light shadow-custom-sm">
        <div className="max-w-7xl mx-auto px-custom-6">
          <div className="flex justify-between items-center h-14">
            <Logo 
              onClick={() => navigate('/')}
              textColor="text-primary"
              hoverTextColor="hover:text-brand-primary"
            />
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-2">
              <button
                onClick={() => scrollToSection('benefits')}
                className="px-custom-3 py-custom-2 text-sm text-secondary hover:text-brand-primary transition-custom font-medium"
              >
                Fördelar
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="px-custom-3 py-custom-2 text-sm text-secondary hover:text-brand-primary transition-custom font-medium"
              >
                Så fungerar det
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="px-custom-3 py-custom-2 text-sm text-secondary hover:text-brand-primary transition-custom font-medium"
              >
                Priser
              </button>
              <button
                onClick={handleContactUs}
                className="px-custom-3 py-custom-2 text-sm text-secondary hover:text-brand-primary transition-custom font-medium"
              >
                Kontakt
              </button>
              <button
                onClick={() => {
                  trackEvent('Login Link Click', { location: 'work-life-balance-nav' });
                  navigate('/login');
                }}
                className="flex items-center space-x-2 px-custom-3 py-custom-2 text-sm text-secondary hover:text-brand-primary transition-custom font-medium"
              >
                <LogIn className="w-4 h-4" />
                <span>Logga in</span>
              </button>
              <button
                onClick={handleGetStarted}
                className="flex items-center space-x-2 px-custom-4 py-custom-2 bg-brand-primary text-inverse radius-custom-lg hover:bg-brand-primary-dark transition-custom shadow-custom-lg text-sm font-medium"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Testa gratis</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <MobileMenu isLandingPage={true} />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 md:pb-12 w-full flex items-center justify-center">
        <div className="inset-0 bg-gradient-to-br from-brand-primary/5 via-brand-secondary/5 to-brand-accent/5"></div>
        <div className="pt-8 sm:pt-12 md:pt-16 text-center text-primary max-w-6xl mx-auto px-custom-4 sm:px-custom-6 md:px-custom-8 relative z-10">
          <h1 className="text-4xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 sm:mb-8 md:mb-10 text-brand-primary dark: text-white leading-tight">
            Förebygg utmattning innan det händer
          </h1>
          
          {/* Enhanced description with icons */}
          <div className="max-w-3xl lg:max-w-4xl mx-auto mb-8 sm:mb-10 md:mb-12">
            <p className="text-lg sm:text-xl md:text-2xl mb-8 sm:mb-10 md:mb-12 text-secondary leading-relaxed text-center">
              Sluta oroa dig för burnout. Få professionell hjälp att hantera arbetsstress, sätta gränser och skapa hållbar work-life balance. 
              Diskret, tillgängligt 24/7, och anpassat för ditt upptagna schema.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-custom-4 sm:gap-custom-6 justify-center mb-8 sm:mb-10 md:mb-12">
            <button 
              onClick={handleGetStarted} 
              className="w-full sm:w-auto px-custom-6 sm:px-custom-8 md:px-custom-10 py-custom-3 sm:py-custom-4 md:py-custom-5 bg-brand-primary text-inverse rounded-lg text-base sm:text-lg md:text-xl font-bold hover:bg-brand-primary-dark transform hover:-translate-y-1 hover:shadow-custom-xl transition-custom duration-300 flex items-center justify-center gap-custom-3 shadow-custom-xl"
            >
              <span>Börja chatta anonymt</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </button>
          </div>

           {/* Enhanced trust indicators with icons */}
          <div className="max-w-4xl mx-auto px-custom-2 sm:px-custom-4 mb-custom-4 sm:mb-custom-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-custom-3 sm:gap-custom-4 text-center">
              <Card 
                variant="small"
                icon={Briefcase}
                iconBgColor="bg-brand-primary"
                title="Workplace-anpassat"
                description="Specialiserat för arbetsstress och work-life balance"
              />
              
              <Card 
                variant="small"
                icon={Shield}
                iconBgColor="bg-brand-secondary"
                title="Karriärsäkert"
                description="Helt konfidentiellt - arbetsgivaren får aldrig reda på det"
              />
              
              <Card 
                variant="small"
                icon={Clock}
                iconBgColor="bg-brand-accent"
                iconTextColor="text-secondary"
                title="Passar ditt schema"
                description="24/7 tillgänglighet - chatta när det passar dig"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Your Therapist Section - Work-Life Balance Context */}
      <section className="py-16 w-full ai-bg">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Work-Life Balance Support som funkar
            </h2>
            <h3 className="text-xl md:text-2xl text-gray-700 mb-8 font-medium">
              Mental hälsovård anpassad för yrkeslivet
            </h3>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Your Therapist hjälper dig hantera arbetsstress och skapa hållbar work-life balance. Du väljer själv hur du vill använda tjänsten:
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card
              icon={Users}
              iconBgColor="bg-brand-secondary"
              iconTextColor="text-white"
              title="Komplement till företagshälsovård"
              description="Använd AI:n som extra stöd vid sidan av din arbetsplats befintliga resurser. Privat och konfidentiellt - utan att påverka din karriär."
              variant="outline"
            />
            
            <Card
              icon={Heart}
              iconBgColor="bg-brand-primary"
              iconTextColor="text-white"
              title="AI + Legitimerad terapeut för arbetsstress"
              description="Starta med AI-terapi för omedelbart stöd och lägg till regelbundna sessioner med terapeut som specialiserar sig på workplace mental health."
              variant="outline"
            />
            
            <Card
              icon={Briefcase}
              iconBgColor="bg-brand-accent"
              iconTextColor="text-secondary"
              title="Enbart AI-terapi för flexibilitet"
              description="Få professionell hjälp med arbetsstress helt genom AI:n. Perfekt när du har begränsat med tid eller vill hantera saker diskret."
              variant="outline"
            />
          </div>
        </div>
      </section>

      {/* Problem Section - Work-specific */}
      <section className="py-16 w-full bg-gradient-to-br from-surface-primary to-surface-secondary">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Känner du igen dig i arbetsstressen?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Timer className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="text-xl font-bold text-primary dark:text-gray-900 mb-4">Alltid på språng</h3>
              <p className="text-secondary dark:text-gray-900 leading-relaxed">
                Konstant brist på tid, omöjliga deadlines och känslan av att aldrig hinna ikapp. Du drömmer om helger men jobbar ändå.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-brand-secondary/10 rounded-2xl flex items-center justify-center mb-6">
                <Battery className="w-8 h-8 text-brand-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary dark:text-gray-900 mb-4">Batteriet är slut</h3>
              <p className="text-secondary dark:text-gray-900 leading-relaxed">
                Trött redan på morgonen. Kaffet hjälper inte längre och du känner dig utmattad även efter semester.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-brand-accent/20 rounded-2xl flex items-center justify-center mb-6">
                <AlertTriangle className="w-8 h-8 text-amber-900" />
              </div>
              <h3 className="text-xl font-bold text-primary dark:text-gray-900 mb-4">Kan inte koppla av</h3>
              <p className="text-secondary dark:text-gray-900 leading-relaxed">
                Tankar på jobbet följer dig hem. Du kollar mail på kvällar och kan inte somna för att hjärnan maler på.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="text-xl font-bold text-primary dark:text-gray-900 mb-4">Svårt säga nej</h3>
              <p className="text-secondary dark:text-gray-900 leading-relaxed">
                Du tar på dig för mycket, säger ja till allt och känner skuld när du försöker sätta gränser.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-brand-secondary/10 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-brand-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary dark:text-gray-900 mb-4">Relationer lidande</h3>
              <p className="text-secondary dark:text-gray-900 leading-relaxed">
                Partner och vänner klagar på att du aldrig är närvarande. Du missar viktiga stunder för jobbet.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-brand-accent/20 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-amber-900" />
              </div>
              <h3 className="text-xl font-bold text-primary dark:text-gray-900 mb-4">Förlorat perspektiv</h3>
              <p className="text-secondary dark:text-gray-900 leading-relaxed">
                Du vet inte längre vad som är viktigt. Allt känns akut och du har tappat känslan för vad du egentligen vill.
              </p>
            </div>
          </div>
        </div>
      </section>

       {/* Solution Presentation Section */}
      <section id="how-it-works" className="py-12 w-full bg-gradient-to-br from-surface-primary to-surface-secondary">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Så här fungerar hybrid-terapin
            </h2>
          </div>
          
          {/* Three step process with visual timeline */}
          <div className="relative">
            {/* Timeline line - hidden on mobile */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-brand-primary via-brand-primary to-brand-primary rounded-full transform -translate-y-1/2 z-0"></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 relative z-10">
              {/* Step 1: Börja direkt */}
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-brand-primary/20 relative">
                <div className="absolute -top-4 left-8 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold text-sm lg:left-1/2 lg:transform lg:-translate-x-1/2">
                  1
                </div>
                <div className="mt-4">
                  <h3 className="text-2xl font-bold text-primary dark:text-gray-900 mb-4">Börja direkt</h3>
                  <p className="text-brand-primary font-semibold mb-4">Ingen väntan</p>
                  
                  <ul className="space-y-3 text-secondary dark:text-gray-900">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary dark:text-gray-900 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Starta en konversation med vår AI-terapeut direkt</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary dark:text-gray-900 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Tränad av legitimerade svenska psykologer</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary dark:text-gray-900 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Tillgänglig 24/7 när du behöver stöd som mest</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 2: Bygg förståelse */}
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-brand-primary/20 relative">
                <div className="absolute -top-4 left-8 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold text-sm lg:left-1/2 lg:transform lg:-translate-x-1/2">
                  2
                </div>
                <div className="mt-4">
                  <h3 className="text-2xl font-bold text-primary dark:text-gray-900 mb-4">Bygg förståelse</h3>
                  <p className="text-brand-primary font-semibold mb-4">Din personliga terapeutiska resa</p>
                  
                  <ul className="space-y-3 text-secondary dark:text-gray-900">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span>AI:n lär känna din unika situation över tid</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span>Gradvis insamling av din historia genom naturliga samtal</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span>Evidensbaserade tekniker anpassade för dig</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 3: Professionell anslutning */}
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-brand-primary/20 relative">
                <div className="absolute -top-4 left-8 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold text-sm lg:left-1/2 lg:transform lg:-translate-x-1/2">
                  3
                </div>
                <div className="mt-4">
                  <h3 className="text-2xl font-bold text-primary dark:text-gray-900 mb-4">Professionell anslutning</h3>
                  <p className="text-brand-primary font-semibold mb-4">När du känner dig redo</p>
                  
                  <ul className="space-y-3 text-secondary dark:text-gray-900">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span>Personlig matchning med legitimerad terapeut</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span>Smidig övergång med AI:ns stöd mellan sessioner</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span>Kontinuerlig vård som bygger på vad du redan delat</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="text-center mt-12">
            <button
              onClick={handleGetStarted}
              className="px-8 py-4 bg-brand-primary text-white rounded-xl text-lg font-semibold hover:from-pink-600 hover:to-red-600 transform hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 mx-auto"
            >
              Starta din resa nu
            </button>
          </div>
      </section>

      {/* First Step Section - Work focused */}
      <section className="py-16 w-full ai-bg">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Det svåraste steget på jobbet är att erkänna att du behöver hjälp
            </h2>
          </div>
          
          <div className="mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-8 text-center">
              Du vet att stressen påverkar dig negativt, men det känns komplicerat att söka hjälp när det gäller jobbet:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">"Kommer det påverka min karriär?"</h4>
                <p className="text-gray-700">Rädsla för att verka svag eller inkompetent inför kollegor och chefer.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">"Har jag tid för terapi?"</h4>
                <p className="text-gray-700">Att boka tid under arbetstid känns omöjligt med alla deadlines.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">"Kanske ska jag bara jobba hårdare?"</h4>
                <p className="text-gray-700">Tanken att problemet löses genom ännu mer ansträngning och övertid.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">"Vad kommer HR att tänka?"</h4>
                <p className="text-gray-700">Oro för att företagshälsovård eller HR får reda på dina problem.</p>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Du behöver inte offra din karriär för din hälsa</h3>
            <p className="text-xl text-gray-700 leading-relaxed">
              Ibland räcker det att börja prata med någon som förstår arbetsmiljöns utmaningar.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 w-full bg-gradient-to-br from-gray-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Kraftfulla verktyg för din mentala hälsa
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card
              icon={Clock}
              iconBgColor="bg-indigo-950"
              iconTextColor="text-white"
              title="24/7 tillgänglighet"
              description="Ångest kommer inte på kontorstid. AI:n finns där när du vaknar mitt i natten, före viktiga möten, eller när vardagen känns övermäktig. Professionell support utan väntetid."
            />
            <Card
              icon={Brain}
              iconBgColor="bg-indigo-900"
              iconTextColor="text-white"
              title="Personlig minnesbank"
              description="AI:n kommer ihåg viktiga genombrott, mönster och strategier som fungerat för dig. Inget går förlorat mellan sessioner."
            />

            <Card
              icon={BarChart3}
              iconBgColor="bg-indigo-800"
              iconTextColor="text-white"
              title="Framsteg som din terapeut ser"
              description="Tydlig spårning av ditt mående som både du och din framtida terapeut kan använda för att skapa den bästa behandlingsplanen."
            />

            <Card
              icon={AlertTriangle}
              iconBgColor="bg-brand-primary"
              iconTextColor="text-white"
              title="Krisstöd med mänsklig backup"
              description="AI:n känner igen krissignaler och kopplar dig omedelbart till legitimerad personal eller akutpsykiatri. Teknologi och mänsklig omsorg arbetar tillsammans."
            />

            <Card
              icon={Users}
              iconBgColor="bg-brand-secondary"
              iconTextColor="text-white"
              title="Sömlös övergång till terapeut och AI"
              description="När du är redo förs din berättelse och framsteg vidare till en legitimerad terapeut som redan förstår din situation från dag ett. AI:n får också feedback från terapisessionerna för att fortsätta stödja dig mellan mötena."
            />

            <Card
              icon={Pill}
              iconBgColor="bg-brand-accent"
              iconTextColor="text-amber-900"
              title="Personaliserade terapeutiska övningar"
              description="AI:n föreslår skräddarsydda verktyg och tekniker baserat på dina specifika utmaningar - från andningsövningar till tankeloggar. Allt anpassat efter vad som fungerar just för dig."
            />

            
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-12 w-full bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Betala bara för det du använder
            </h2>
            <p className="text-xl text-gray-600">
              Köp credits när du behöver dem • ingen bindningstid eller månadskostnader
            </p>
            <p className="text-xl text-gray-600">
              Credits förfaller aldrig • 1 credit = 1 meddelande och svar
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Prova Plan */}
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-300 hover:shadow-xl hover:border-gray-400 transition-all duration-300 flex flex-col h-full">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Prova på</h3>
                <div className="mb-1">
                  <span className="text-3xl font-bold text-gray-900">49 kr</span>
                </div>
              </div>
              
              <div className="space-y-3 mb-6 flex-grow">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-amber-900 flex-shrink-0" />
                  <span className="text-sm text-gray-700">50 credits - engångsbetalning</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-amber-900 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Riskmonitor</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-amber-900 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Krypterad chat</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-amber-900 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Terapeutmatchning</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-amber-00 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Personlig AI-terapeut 24/7</span>
                </div>
              </div>
              
              <button
                onClick={handleGetStarted}
                className="w-full px-6 py-3 bg-brand-accent text-amber-9900 rounded-xl font-semibold hover:from-gray-700 hover:to-gray-800 transition-all duration-300 mt-auto hover:cursor-pointer"
              >
                Prova gratis
              </button>
            </div>

            {/* Start Plan - Nu populärast */}
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-300 hover:shadow-xl hover:border-gray-400 transition-all duration-300 relative flex flex-col h-full">
              
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Komma igång</h3>
                <div className="mb-1">
                  <span className="text-3xl font-bold text-gray-900">99 kr</span>
                </div>
              </div>
              
              <div className="space-y-3 mb-6 flex-grow">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-amber-900 flex-shrink-0" />
                  <span className="text-sm text-gray-700">150 credits - engångsbetalning</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-secondary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Riskmonitor</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-secondary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Krypterad chat</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-secondary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Terapeutmatchning</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-secondary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Prioriterat stöd</span>
                </div>
              </div>
              
              <button
                onClick={handleGetStarted}
                className="w-full px-6 py-3 bg-brand-secondary text-white rounded-xl font-semibold mt-auto hover:cursor-pointer"
              >
                Prova gratis
              </button>
            </div>

            {/* Power Plan */}
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-300 hover:shadow-xl hover:border-gray-400 transition-all duration-300 flex flex-col h-full">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Daglig användning</h3>
                <div className="mb-1">
                  <span className="text-3xl font-bold text-gray-900">349 kr</span>
                </div>
              </div>
              
              <div className="space-y-3 mb-6 flex-grow">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-amber-900 flex-shrink-0" />
                  <span className="text-sm text-gray-700">1000 credits - engångsbetalning</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-primary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Riskmonitor</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-primary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Krypterad chat</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-primary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Terapeutmatchning</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-primary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Alla premium-funktioner</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-primary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Direktkontakt med terapeuter</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-brand-primary flex-shrink-0" />
                  <span className="text-sm text-gray-700">Personlig vårdplan</span>
                </div>
              </div>
              
              <button
                onClick={handleGetStarted}
                className="w-full px-6 py-3 bg-brand-primary text-white rounded-xl font-semibold mt-auto hover:cursor-pointer"
              >
                Prova gratis
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Direct Therapist Booking Section */}
      <section className="py-16 w-full bg-brand-primary">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Boka direkt med våra legitimerade terapeuter
              </h2>
              <p className="text-xl leading-relaxed mb-8 text-white/90">
                Vill du hoppa direkt till traditionell terapi? Du kan gärna kontakta oss för direkt bokning av terapisamtal med våra legitimerade terapeuter. Ingen AI mellansteg - bara professionell vård från dag ett.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                  <span className="text-white/90">Legitimerade svenska psykoterapeuter och psykologer</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                  <span className="text-white/90">Personlig matchning baserat på dina behov</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                  <span className="text-white/90">Flexibla tider för video- eller telefonsamtal</span>
                </div>
              </div>

              <button
                onClick={handleContactUs}
                className="px-8 py-4 bg-white text-brand-primary rounded-xl text-lg font-semibold hover:bg-gray-100 transform hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex items-center gap-3"
              >
                <MessageCircle className="w-5 h-5" />
                Kontakta oss för bokning
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {/* Image */}
            <div className="relative flex justify-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 max-w-xs">
                <img 
                  src="/images/jenny-fuxe.jpg" 
                  alt="Jenny Fuxe - Legitimerad psykoterapeut och grundare"
                  className="w-full h-48 object-cover rounded-xl shadow-lg"
                />
                <div className="mt-4 text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">Jenny Fuxe</h3>
                  <p className="text-white/80 text-sm">Legitimerad psykoterapeut och grundare</p>
                  <p className="text-white/70 text-xs mt-1">Specialiserad på ångest, depression och självkänsla</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 w-full bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Vanliga frågor från anställda
            </h2>
          </div>
          
          <div className="space-y-6">
            {workLifeFaqData.map((faq, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full text-left flex justify-between items-center"
                >
                  <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  <ArrowRight 
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      expandedFaq === index ? 'rotate-90' : ''
                    }`} 
                  />
                </button>
                {expandedFaq === index && (
                  <p className="text-gray-700 leading-relaxed mt-4">{faq.answer}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-custom-24 w-full bg-brand-primary text-center text-inverse">
        <div className="max-w-5xl mx-auto px-custom-6">
          <div className="mb-custom-8 flex justify-center">
            <div className="w-custom-20 h-custom-20 bg-white backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center">
              <Briefcase className="w-custom-10 h-custom-10 text-brand-primary" />
            </div>
          </div>
          <h2 className="text-heading-h2 md:text-heading-hero font-bold mb-custom-6">
            Ta kontroll över din arbetsstress
          </h2>
          <p className="text-xl mb-12 text-white/90 max-w-3xl mx-auto leading-relaxed">
            Sluta låt jobbet styra ditt liv. Börja bygga hållbar work-life balance idag.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <button 
              onClick={handleGetStarted} 
              className="px-12 py-6 bg-white text-brand-primary rounded-xl text-xl font-bold hover:bg-gray-100 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 shadow-xl"
            >
              <MessageCircle className="w-6 h-6" />
              Starta anonymt nu
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
              <div className="text-3xl font-bold mb-2">15</div>
              <div className="text-white/80">Gratis chattar</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
              <div className="text-3xl font-bold mb-2">24/7</div>
              <div className="text-white/80">Support tillgänglig</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
              <div className="text-3xl font-bold mb-2">Engångsköp</div>
              <div className="text-white/80">Inga månadsavgifter</div>
            </div>
          </div>
        </div>
      </section>

      {/* Structured Data for SEO */}
      <OrganizationStructuredData />
      <MedicalBusinessStructuredData />
      <WebSiteStructuredData />
      <FAQStructuredData faqs={workLifeFaqData} />
    </div>
  );
};

export default WorkLifeBalancePage;
